// src/features/system/developer/components/video-gallery/CalendarJumpButton.tsx
// ────────────────────────────────────────────────────────────────────────────
// Minimal glass-pill calendar affordance for the Video surface. Inspired by
// the YT/IG calendar pickers and the "jump to date" pattern from TikTok
// creator analytics — the button is transparent + iconic so it never
// competes with playback content, and the popover only lights up days that
// actually carry videos (no dead clicks, no false promises).
//
// Contract:
//   • `daysWithVideos` — sorted ASC list of epoch-ms at LOCAL 00:00 for every
//     day that has at least one item.
//   • `selectedDayMs`  — currently pinned day (or null to clear).
//   • `onSelect`       — called with the day's 00:00 ms (or null to clear).
//
// All months around the data range are reachable via chevrons. We render
// 6 weeks of cells so the layout never reflows when month length changes.
// ────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface Props {
  daysWithVideos: number[];
  selectedDayMs:  number | null;
  onSelect:       (dayMs: number | null) => void;
}

function startOfDay(d: Date): Date {
  const c = new Date(d.getTime());
  c.setHours(0, 0, 0, 0);
  return c;
}

function fmtMonthLabel(d: Date): string {
  return d.toLocaleString(undefined, { month: 'long', year: 'numeric' });
}

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

export default function CalendarJumpButton({ daysWithVideos, selectedDayMs, onSelect }: Props) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  // Popover is portaled to document.body so it escapes any ancestor
  // stacking context (the gallery header uses backdrop-blur which creates
  // one — previously trapped the popover behind the video grid). We track
  // the anchor's viewport rect to position the portal beneath the button.
  const [popoverPos, setPopoverPos] = useState<{ top: number; right: number } | null>(null);
  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const a = anchorRef.current;
      if (!a) return;
      const r = a.getBoundingClientRect();
      setPopoverPos({
        top:   r.bottom + 8, // mt-2 (8px gap)
        right: window.innerWidth - r.right,
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  // Day-set membership lookup in O(1).
  const daySet = useMemo(() => new Set(daysWithVideos), [daysWithVideos]);

  // Default viewport month: selected day's month → newest-content month → today.
  const initialMonth = useMemo(() => {
    if (selectedDayMs != null) {
      const d = new Date(selectedDayMs);
      return new Date(d.getFullYear(), d.getMonth(), 1);
    }
    if (daysWithVideos.length) {
      const d = new Date(daysWithVideos[daysWithVideos.length - 1]);
      return new Date(d.getFullYear(), d.getMonth(), 1);
    }
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), 1);
  }, [selectedDayMs, daysWithVideos]);

  const [viewMonth, setViewMonth] = useState<Date>(initialMonth);
  useEffect(() => { if (open) setViewMonth(initialMonth); }, [open, initialMonth]);

  // Click-outside / Esc to dismiss. The popover is portaled outside
  // `anchorRef`, so also exempt clicks inside the portaled popover.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (anchorRef.current?.contains(t)) return;
      if (popoverRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown',   onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown',   onKey);
    };
  }, [open]);

  // Build 6 weeks × 7 days grid for the current view month.
  const cells = useMemo(() => {
    const firstOfMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
    const startOffset  = firstOfMonth.getDay(); // 0 = Sun
    const gridStart    = new Date(firstOfMonth);
    gridStart.setDate(firstOfMonth.getDate() - startOffset);
    const out: { date: Date; inMonth: boolean; ms: number; hasVideo: boolean }[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      const ms = startOfDay(d).getTime();
      out.push({
        date:    d,
        inMonth: d.getMonth() === viewMonth.getMonth(),
        ms,
        hasVideo: daySet.has(ms),
      });
    }
    return out;
  }, [viewMonth, daySet]);

  const goPrev = useCallback(() => setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1)), []);
  const goNext = useCallback(() => setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1)), []);

  const isToday = (ms: number): boolean => {
    const t = startOfDay(new Date()).getTime();
    return ms === t;
  };

  return (
    <div ref={anchorRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={selectedDayMs != null ? 'Change selected day' : 'Jump to day'}
        title={selectedDayMs != null
          ? `Jump to day: ${new Date(selectedDayMs).toLocaleDateString()}`
          : 'Jump to day'}
        className={[
          'flex items-center justify-center gap-1.5 rounded-full',
          'h-8 px-2.5',
          'bg-black/50 hover:bg-black/70 border border-white/15 hover:border-white/30',
          'backdrop-blur-md text-white/90',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-neon_orange-300/40',
          'transition-colors',
          selectedDayMs != null ? 'border-neon_orange-300/60 text-neon_orange-200' : '',
        ].join(' ')}
      >
        <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden>
          <path d="M7 2v2H4a1 1 0 0 0-1 1v15a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1h-3V2h-2v2H9V2H7zm-2 8h14v10H5V10zm0-4v2h14V6H5z" />
        </svg>
        {selectedDayMs != null && (
          <span className="text-[11px] font-medium tabular-nums">
            {new Date(selectedDayMs).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
          </span>
        )}
      </button>

      {open && popoverPos && createPortal(
        <div
          ref={popoverRef}
          role="dialog"
          aria-label="Pick a day"
          style={{ position: 'fixed', top: popoverPos.top, right: popoverPos.right }}
          className="z-[200] w-[19rem] rounded-xl border border-white/15
                     bg-neutral-950/95 backdrop-blur-xl shadow-2xl p-3 select-none"
        >
          {/* Month header */}
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={goPrev}
              aria-label="Previous month"
              className="w-7 h-7 flex items-center justify-center rounded-md
                         text-white/70 hover:text-white hover:bg-white/10
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-neon_orange-300/40"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden>
                <path d="M15.4 7.4L14 6l-6 6 6 6 1.4-1.4L10.8 12z" />
              </svg>
            </button>
            <div className="text-[12px] font-semibold text-white/90 tabular-nums">
              {fmtMonthLabel(viewMonth)}
            </div>
            <button
              type="button"
              onClick={goNext}
              aria-label="Next month"
              className="w-7 h-7 flex items-center justify-center rounded-md
                         text-white/70 hover:text-white hover:bg-white/10
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-neon_orange-300/40"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden>
                <path d="M8.6 7.4L10 6l6 6-6 6-1.4-1.4L13.2 12z" />
              </svg>
            </button>
          </div>

          {/* Weekday header */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {WEEKDAYS.map((w, i) => (
              <div key={i} className="text-center text-[10px] uppercase tracking-wide text-white/40">
                {w}
              </div>
            ))}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 gap-1">
            {cells.map((c, i) => {
              const selected = selectedDayMs === c.ms;
              const baseDim  = !c.inMonth;
              const clickable = c.hasVideo;
              return (
                <button
                  key={i}
                  type="button"
                  disabled={!clickable}
                  onClick={() => { onSelect(c.ms); setOpen(false); }}
                  className={[
                    'aspect-square text-[12px] rounded-md flex items-center justify-center relative',
                    'transition-colors tabular-nums focus:outline-none focus-visible:ring-2 focus-visible:ring-neon_orange-300/40',
                    selected
                      ? 'bg-neon_orange-300/30 text-white border border-neon_orange-300/70'
                      : clickable
                        ? 'text-white hover:bg-white/10 border border-white/10'
                        : 'text-white/25 cursor-not-allowed border border-transparent',
                    baseDim && !selected ? 'opacity-60' : '',
                  ].join(' ')}
                  aria-pressed={selected}
                  aria-label={c.date.toDateString() + (clickable ? '' : ' (no videos)')}
                  title={c.date.toLocaleDateString()}
                >
                  <span>{c.date.getDate()}</span>
                  {clickable && !selected && (
                    <span
                      aria-hidden
                      className={[
                        'absolute bottom-1 w-1 h-1 rounded-full',
                        isToday(c.ms) ? 'bg-white' : 'bg-neon_orange-300/80',
                      ].join(' ')}
                    />
                  )}
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div className="mt-3 flex items-center justify-between">
            <button
              type="button"
              onClick={() => {
                const t = new Date();
                setViewMonth(new Date(t.getFullYear(), t.getMonth(), 1));
              }}
              className="text-[11px] text-white/70 hover:text-white px-2 py-1 rounded
                         hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-neon_orange-300/40"
            >
              Today
            </button>
            <button
              type="button"
              disabled={selectedDayMs == null}
              onClick={() => { onSelect(null); setOpen(false); }}
              className={[
                'text-[11px] px-2 py-1 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-neon_orange-300/40',
                selectedDayMs == null
                  ? 'text-white/25 cursor-not-allowed'
                  : 'text-rose-200 hover:text-rose-100 hover:bg-rose-500/10',
              ].join(' ')}
            >
              Clear
            </button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
